import {
  AdSlotItem,
  ProtectedCalculationZoneConfig,
  AdsTxtEntryItem,
  RevenueEventItem,
  AffiliateConversionItem,
  CommercialRelationshipItem,
  MonetizationExperimentItem,
  RevenueOpportunityItem,
  MonetizationIncidentItem,
  MonetizationTelemetry,
  DeliverableP10
} from '../types';

export const INITIAL_TELEMETRY: MonetizationTelemetry = {
  activeMode: 'EZOIC_WITH_ADSENSE_MEDIATION',
  activeProvider: 'EZOIC',
  totalRevenue30dUsd: 48920.50,
  adRevenue30dUsd: 31450.20,
  affiliateRevenue30dUsd: 14870.30,
  sponsorshipRevenue30dUsd: 2600.00,
  averagePageRpmUsd: 8.42,
  averageEpmvUsd: 14.85,
  revenuePer1kSessionsUsd: 18.20,
  topMonetizedMarket: 'United States (US)',
  topMonetizedTool: 'Salary & Income Tax Engine (Prompt 06)',
  consentAcceptanceRatePct: 82.4,
  adsTxtStatus: 'HEALTHY',
  cwvGuardrailsStatus: 'ALL_PASSING',
  activeIncidentsCount: 0,
  organicRankingIndependenceVerified: true,
};

export const INITIAL_AD_INVENTORY: AdSlotItem[] = [
  {
    id: 'slot_tool_after_result',
    placementKey: 'tool_after_result',
    label: 'Primary Tool Output Bottom Anchor',
    pageType: 'TOOL',
    position: 'Below primary calculation result breakdown & immediate explanation',
    devices: ['desktop', 'tablet', 'mobile'],
    provider: 'EZOIC',
    allowedFormats: ['banner_responsive', 'rectangle_300x250', 'leaderboard_728x90'],
    reservedDimensions: { width: 728, height: 90, minHeight: 90 },
    consentRequirements: ['advertising_storage', 'ad_personalization'],
    enabled: true,
    priority: 1,
    monetizationPolicy: 'ENABLED',
    viewabilityRate: 84.2,
    estimatedRpm: 6.80,
    clsImpact: 0.000,
  },
  {
    id: 'slot_guide_in_content_1',
    placementKey: 'guide_in_content_1',
    label: 'Financial Guide In-Content Top',
    pageType: 'GUIDES',
    position: 'Between Section 2 and 3 of editorial guides (>600px into content)',
    devices: ['desktop', 'tablet', 'mobile'],
    provider: 'EZOIC',
    allowedFormats: ['display_auto', 'rectangle_300x250'],
    reservedDimensions: { width: 336, height: 280, minHeight: 250 },
    consentRequirements: ['advertising_storage', 'ad_personalization'],
    enabled: true,
    priority: 2,
    monetizationPolicy: 'ENABLED',
    viewabilityRate: 79.5,
    estimatedRpm: 12.40,
    clsImpact: 0.001,
  },
  {
    id: 'slot_desktop_sidebar_1',
    placementKey: 'desktop_sidebar_1',
    label: 'Desktop Right Auxiliary Rail',
    pageType: 'CURRENCY',
    position: 'Fixed right column below currency pair metadata and quick tools',
    devices: ['desktop'],
    provider: 'GOOGLE_ADSENSE',
    allowedFormats: ['rectangle_300x250'],
    reservedDimensions: { width: 300, height: 250, minHeight: 250 },
    consentRequirements: ['advertising_storage'],
    enabled: true,
    priority: 3,
    monetizationPolicy: 'ENABLED',
    viewabilityRate: 91.0,
    estimatedRpm: 5.10,
    clsImpact: 0.000,
  },
  {
    id: 'slot_mobile_between_sections_1',
    placementKey: 'mobile_between_sections_1',
    label: 'Mobile Section Divider In-Feed',
    pageType: 'SALARY',
    position: 'Below Tax Bracket Breakdown table, above Methodology disclosures',
    devices: ['mobile'],
    provider: 'EZOIC',
    allowedFormats: ['large_mobile_320x100', 'rectangle_300x250'],
    reservedDimensions: { width: 320, height: 100, minHeight: 100 },
    consentRequirements: ['advertising_storage', 'ad_personalization'],
    enabled: true,
    priority: 4,
    monetizationPolicy: 'LIMITED',
    viewabilityRate: 76.1,
    estimatedRpm: 4.25,
    clsImpact: 0.002,
  },
  {
    id: 'slot_footer_multiplex',
    placementKey: 'footer_multiplex',
    label: 'Footer Native Recommendation Grid',
    pageType: 'HOMEPAGE',
    position: 'Above global footer links, below bottom call-to-action banner',
    devices: ['desktop', 'tablet', 'mobile'],
    provider: 'GOOGLE_ADSENSE',
    allowedFormats: ['multiplex_native'],
    reservedDimensions: { width: 970, height: 250, minHeight: 200 },
    consentRequirements: ['advertising_storage'],
    enabled: true,
    priority: 5,
    monetizationPolicy: 'ENABLED',
    viewabilityRate: 68.3,
    estimatedRpm: 3.90,
    clsImpact: 0.001,
  },
  {
    id: 'slot_anchor_mobile',
    placementKey: 'anchor_mobile',
    label: 'Mobile Sticky Bottom Anchor Bar',
    pageType: 'LOAN',
    position: 'Fixed screen bottom (z-index: 40) with dismiss control',
    devices: ['mobile'],
    provider: 'EZOIC',
    allowedFormats: ['sticky_anchor'],
    reservedDimensions: { width: 320, height: 50, minHeight: 50 },
    consentRequirements: ['advertising_storage', 'ad_personalization'],
    enabled: true,
    priority: 6,
    monetizationPolicy: 'PRIVACY_SENSITIVE',
    viewabilityRate: 98.2,
    estimatedRpm: 9.15,
    clsImpact: 0.000,
  },
];

export const PROTECTED_ZONES: ProtectedCalculationZoneConfig[] = [
  {
    id: 'prot_currency_converter',
    toolView: 'currency-intelligence',
    zoneName: 'Currency Converter Primary Calculation Box',
    elementsProtected: ['Amount input', 'From/To currency selectors', 'Swap button', 'Live Rate output', 'Converted total display'],
    noAdInjectReason: 'Prevents misclicks between currency selection and result generation.',
    verifiedClean: true,
  },
  {
    id: 'prot_salary_engine',
    toolView: 'salary-engine',
    zoneName: 'Salary & Income Tax Input/Output Matrix',
    elementsProtected: ['Gross Pay input', 'Jurisdiction selector', 'Filing Status options', 'Take-Home Pay Net Total', 'Instant Deduction Bar'],
    noAdInjectReason: 'YMYL protection; financial result integrity must be completely unhindered by ad units.',
    verifiedClean: true,
  },
  {
    id: 'prot_loan_engine',
    toolView: 'loan-engine',
    zoneName: 'Mortgage & Loan PITI Calculation Core',
    elementsProtected: ['Principal input', 'Interest rate field', 'Term slider', 'Monthly Payment display', 'PITI breakdown pie'],
    noAdInjectReason: 'Avoids lender deception; ensures ad content is never confused with YamooPay financial advice.',
    verifiedClean: true,
  },
  {
    id: 'prot_my_yamoopay',
    toolView: 'my-yamoopay',
    zoneName: 'My YamooPay Private Workspace & Security Center',
    elementsProtected: ['Passkey manager', 'Saved scenarios', 'Session list', 'Data export trigger', 'Privacy Center'],
    noAdInjectReason: 'Absolute zero ad policy on authenticated user private workspace surfaces.',
    verifiedClean: true,
  },
];

export const INITIAL_ADS_TXT_ENTRIES: AdsTxtEntryItem[] = [
  {
    id: 'at_1',
    domain: 'google.com',
    publisherId: 'pub-4472565410092962',
    relationshipType: 'DIRECT',
    certificationId: 'f08c47fec0942fa0',
    providerName: 'Google AdSense (Client ID: 4709387783)',
    status: 'VALID',
    lastAuditedAt: '2026-08-25T08:00:00Z',
  },
  {
    id: 'at_2',
    domain: 'ezoic.com',
    publisherId: '104829',
    relationshipType: 'DIRECT',
    certificationId: 'd39a0293f1',
    providerName: 'Ezoic Monetization Platform',
    status: 'VALID',
    lastAuditedAt: '2026-08-24T08:00:00Z',
  },
  {
    id: 'at_3',
    domain: 'rubiconproject.com',
    publisherId: '18294',
    relationshipType: 'RESELLER',
    certificationId: '0bfb60882e382d56',
    providerName: 'Magnite / Ezoic SSP Network',
    status: 'VALID',
    lastAuditedAt: '2026-08-24T08:00:00Z',
  },
  {
    id: 'at_4',
    domain: 'appnexus.com',
    publisherId: '8492',
    relationshipType: 'RESELLER',
    certificationId: '3702584d3d1f',
    providerName: 'Xandr / Microsoft Advertising',
    status: 'VALID',
    lastAuditedAt: '2026-08-24T08:00:00Z',
  },
];

export const INITIAL_REVENUE_EVENTS: RevenueEventItem[] = [
  {
    id: 'rev_101',
    source: 'EZOIC',
    providerName: 'Ezoic Monetization Platform',
    type: 'AD_IMPRESSION_REVENUE',
    grossAmount: 1240.50,
    currency: 'USD',
    normalizedAmountUsd: 1240.50,
    eventDate: '2026-08-23',
    pageType: 'Salary Calculator',
    market: 'US',
    deviceClass: 'desktop',
    status: 'FINALIZED',
  },
  {
    id: 'rev_102',
    source: 'GOOGLE_ADSENSE',
    providerName: 'Google AdSense Mediation',
    type: 'AD_IMPRESSION_REVENUE',
    grossAmount: 890.20,
    currency: 'USD',
    normalizedAmountUsd: 890.20,
    eventDate: '2026-08-23',
    pageType: 'Currency Pair USD-EUR',
    market: 'US / EU',
    deviceClass: 'mobile',
    status: 'FINALIZED',
  },
  {
    id: 'rev_103',
    source: 'NONE',
    providerName: 'Wise Partner Affiliate',
    type: 'AFFILIATE_COMMISSION',
    grossAmount: 480.00,
    currency: 'EUR',
    normalizedAmountUsd: 523.20,
    eventDate: '2026-08-23',
    pageType: 'Transfer Corridor US-IN',
    market: 'US-IN',
    deviceClass: 'desktop',
    status: 'APPROVED',
  },
  {
    id: 'rev_104',
    source: 'DIRECT_SPONSORSHIP',
    providerName: 'Revolut Business Placement',
    type: 'SPONSORSHIP',
    grossAmount: 2600.00,
    currency: 'USD',
    normalizedAmountUsd: 2600.00,
    eventDate: '2026-08-20',
    pageType: 'Sponsorship Guide',
    market: 'UK / EU',
    deviceClass: 'desktop',
    status: 'PAID',
  },
];

export const INITIAL_AFFILIATE_CONVERSIONS: AffiliateConversionItem[] = [
  {
    id: 'aff_1',
    clickId: 'clk_wz_882910',
    providerId: 'wise',
    providerName: 'Wise (formerly TransferWise)',
    corridorOrTool: 'US -> IN Corridor',
    sendAmount: 1000,
    conversionType: 'CPA',
    value: 1000,
    commissionAmount: 18.50,
    currency: 'USD',
    normalizedCommissionUsd: 18.50,
    status: 'APPROVED',
    occurredAt: '2026-08-24T10:12:00Z',
    approvedAt: '2026-08-24T12:00:00Z',
  },
  {
    id: 'aff_2',
    clickId: 'clk_rm_492019',
    providerId: 'remitly',
    providerName: 'Remitly Global',
    corridorOrTool: 'US -> PH Corridor',
    sendAmount: 500,
    conversionType: 'CPA',
    value: 500,
    commissionAmount: 15.00,
    currency: 'USD',
    normalizedCommissionUsd: 15.00,
    status: 'APPROVED',
    occurredAt: '2026-08-24T09:45:00Z',
    approvedAt: '2026-08-24T11:30:00Z',
  },
  {
    id: 'aff_3',
    clickId: 'clk_wu_102938',
    providerId: 'western-union',
    providerName: 'Western Union',
    corridorOrTool: 'FR -> MA Corridor',
    sendAmount: 300,
    conversionType: 'CPA',
    value: 300,
    commissionAmount: 12.00,
    currency: 'EUR',
    normalizedCommissionUsd: 13.08,
    status: 'PENDING',
    occurredAt: '2026-08-24T13:20:00Z',
  },
];

export const INITIAL_COMMERCIAL_RELATIONSHIPS: CommercialRelationshipItem[] = [
  {
    id: 'com_1',
    partnerName: 'Ezoic Monetization Platform & Google MCM',
    type: 'AD_NETWORK',
    effectiveFrom: '2025-01-01',
    markets: ['Global'],
    agreementOwner: 'Chief Monetization Officer',
    disclosureRequired: true,
    disclosureText: 'Display ads served by Ezoic / Google AdSense.',
    commissionModel: 'Header Bidding Revenue Share',
    status: 'ACTIVE',
    organicRankingIndependenceVerified: true,
  },
  {
    id: 'com_2',
    partnerName: 'Wise Partner Network',
    type: 'AFFILIATE',
    effectiveFrom: '2024-06-01',
    markets: ['US', 'UK', 'EU', 'CA', 'AU'],
    agreementOwner: 'Senior Affiliate Strategist',
    disclosureRequired: true,
    disclosureText: 'YamooPay may earn a commission from partner links. Organic rankings remain 100% independent.',
    commissionModel: 'CPA ($18.50 per new active user)',
    status: 'ACTIVE',
    organicRankingIndependenceVerified: true,
  },
  {
    id: 'com_3',
    partnerName: 'Remitly Affiliates',
    type: 'AFFILIATE',
    effectiveFrom: '2024-08-15',
    markets: ['US', 'CA', 'GB', 'DE'],
    agreementOwner: 'Senior Affiliate Strategist',
    disclosureRequired: true,
    disclosureText: 'YamooPay may earn a commission when you send money using Remitly.',
    commissionModel: 'CPA ($15.00 per first transfer)',
    status: 'ACTIVE',
    organicRankingIndependenceVerified: true,
  },
];

export const INITIAL_EXPERIMENTS: MonetizationExperimentItem[] = [
  {
    id: 'exp_1',
    name: 'Lazy-Loading Threshold for In-Content Guide Ads',
    hypothesis: 'Delaying ad slot initialization until 300px before viewport reduces main-thread blocking by 120ms without impacting viewability.',
    pageType: 'Financial Guides (YMYL)',
    variableTested: 'IntersectionObserver RootMargin (100px vs 300px)',
    controlCohort: '100px pre-render',
    treatmentCohort: '300px pre-render',
    primaryMetric: 'LCP (Largest Contentful Paint) latency ms',
    guardrailMetrics: [
      { metric: 'Viewability Rate', threshold: '>75%', currentVal: '81.2%', passed: true },
      { metric: 'Ad RPM Usd', threshold: '>$10.00', currentVal: '$12.40', passed: true },
      { metric: 'CLS Impact', threshold: '<0.005', currentVal: '0.001', passed: true },
    ],
    status: 'PASSED',
    startedAt: '2026-08-10',
    estimatedRevenueUpliftPct: +4.2,
    calculatorCompletionDeltaPct: +0.0,
    clsDelta: -0.002,
  },
];

export const INITIAL_OPPORTUNITIES: RevenueOpportunityItem[] = [
  {
    id: 'opp_1',
    title: 'Enable Ezoic Mediation for UK & EU Currency Pair Traffic',
    type: 'MEDIATION',
    estimatedUpsideUsdMonthly: 3200,
    implementationCost: 'LOW',
    uxRisk: 'LOW',
    complianceRisk: 'ZERO',
    status: 'OPEN',
    priorityScore: 92,
  },
  {
    id: 'opp_2',
    title: 'Expand Wise & Remitly Affiliate Deep Links on CAD-INR Corridor',
    type: 'AFFILIATE_OPTIMIZATION',
    estimatedUpsideUsdMonthly: 1850,
    implementationCost: 'LOW',
    uxRisk: 'LOW',
    complianceRisk: 'ZERO',
    status: 'OPEN',
    priorityScore: 88,
  },
];

export const INITIAL_INCIDENTS: MonetizationIncidentItem[] = [
  {
    id: 'inc_101',
    incidentNumber: 'MON-INC-2026-004',
    severity: 'LOW',
    providerOrSlot: 'Mobile Anchor Slot',
    title: 'Minor layout adjustment resolved on mobile screen viewports <360px',
    detectedAt: '2026-08-22T14:10:00Z',
    status: 'RESOLVED',
    actionTaken: 'Enforced sticky anchor bottom z-index token (z-40) below mobile drawer.',
    affectedMarkets: ['Global Mobile'],
  },
];

export const DELIVERABLES_P10: DeliverableP10[] = [
  {
    code: 'DEL-P10-01',
    titleEn: 'Commercial Governance & Organic Ranking Integrity Firewall',
    titleFr: 'Gouvernance Commerciale & Pare-feu d\'Indépendance du Classement Organique',
    category: 'Commercial Governance & Ranking Integrity',
    summary: 'Strict separation between product truth, organic ranking algorithms (User Value Score), and commercial affiliate models.',
    details: [
      'Automated unit test `organicRankingShouldNotDependOnCommission` enforcing zero weight for commission values in organic sort.',
      'Mandatory `sponsored`rel attributes and explicit "Sponsored" badges for paid placements.',
      'Comprehensive Commercial Disclosure framework explaining affiliate commissions across all market locales.',
      'Zero pay-to-win guarantee prohibiting financial sponsors from purchasing top organic comparison positions.',
    ],
    architectureNotes: 'Enforces pure commercial isolation. The User Value Score equation remains untouched by affiliate commissions.',
  },
  {
    code: 'DEL-P10-02',
    titleEn: 'AdSlot Abstraction & Multi-Provider Monetization Adapter',
    titleFr: 'Abstraction AdSlot & Adaptateur Multi-Régies de Monétisation',
    category: 'Monetization Architecture & AdSlot Abstraction',
    summary: 'Decoupled AdSlot component supporting Google AdSense, Ezoic managed header bidding, and direct sponsorships without code scatter.',
    details: [
      'Generic `<AdSlot />` component accepting placement keys, allowed formats, and consent prerequisites.',
      'MonetizationMode toggle (`ADSENSE_ONLY`, `EZOIC_MANAGED`, `EZOIC_WITH_ADSENSE_MEDIATION`, `ADS_DISABLED`, `TEST_MODE`).',
      'Instant Emergency Kill-Switch for instant ad suppression across page types, markets, or devices.',
      'Exact dimension reservation (width & height min-height containers) ensuring minimal CLS impact (<0.005).',
    ],
    architectureNotes: 'Centralized ad logic prevents direct AdSense/Ezoic script clutter in calculation view components.',
  },
  {
    code: 'DEL-P10-03',
    titleEn: 'Protected Calculation Zones & YMYL Ad Density Safeguards',
    titleFr: 'Zones de Calcul Protégées & Garde-fous de Densité Publicitaire YMYL',
    category: 'Protected Calculation Zones & Ad Density',
    summary: 'Strict spatial perimeter around primary financial inputs and outputs where external advertisements are strictly prohibited.',
    details: [
      'Absolute prohibition of ads between Input fields, Action buttons, and Financial Results.',
      'Zero ad policy across My YamooPay private workspace, authentication modals, security centers, and privacy controls.',
      'Accidental click prevention with strict 32px+ touch-target buffers between calculation buttons and ad units.',
      'Strict ad density ceiling preventing intrusive ad floods on short calculation pages.',
    ],
    architectureNotes: 'Guarantees unhindered usability for core financial calculations while preserving user trust.',
  },
  {
    code: 'DEL-P10-04',
    titleEn: 'Consent Management & Privacy Signals (Google-Certified TCF v2.2 & GPP)',
    titleFr: 'Gestion du Consentement & Signaux de Confidentialité (TCF v2.2 Certifié Google & GPP)',
    category: 'Consent Management & Privacy Signals (TCF / GPP)',
    summary: 'Unified Google-certified CMP integration supporting IAB Europe TCF v2.2 for EEA/UK/CH and Global Privacy Platform for US states.',
    details: [
      'Single non-overlapping CMP experience avoiding duplicate popups or consent conflicts.',
      'Automatic routing to non-personalized or limited ad requests when explicit consent is withheld.',
      'Persistent "Cookie & Privacy Preferences" footer trigger enabling instantaneous consent revocation.',
      'Complete audit trail storing anonymized consent records with versioning in compliance with GDPR and US Privacy laws.',
    ],
    architectureNotes: 'Integrated with Prompt 08 Privacy Center. Rejecting consent never restricts access to financial tools.',
  },
  {
    code: 'DEL-P10-05',
    titleEn: 'ads.txt Governance & Automated Supply-Chain Security',
    titleFr: 'Gouvernance ads.txt & Sécurité Automatisée de la Chaîne d\'Approvisionnement',
    category: 'Google AdSense & Ezoic Integration',
    summary: 'Automated ads.txt generator and live validator enforcing authorized seller verification across Google and Ezoic SSP networks.',
    details: [
      'Live syntax checker, duplicate entry detection, and unauthorized seller flagging.',
      'Full support for DIRECT and RESELLER relationship designations with Google certification IDs.',
      'Command Center status indicator alerting engineering teams to missing required seller entries.',
      'Automated supply chain integrity audits preventing domain spoofing and ad fraud.',
    ],
    architectureNotes: 'Serves standard /ads.txt directly from backend routing configuration.',
  },
  {
    code: 'DEL-P10-06',
    titleEn: 'Monetization Command Center & Revenue Intelligence Dashboard',
    titleFr: 'Centre de Commandement de Monétisation & Tableau de Bord d\'Intelligence Revenu',
    category: 'Monetization Command Center & Revenue Intelligence',
    summary: 'Real-time revenue operations dashboard monitoring Page RPM, EPMV, affiliate conversions, and Core Web Vitals guardrails.',
    details: [
      'Executive KPI overview: Total Revenue, Ad Revenue, Affiliate Earnings, Page RPM vs EPMV, Revenue per 1,000 Sessions.',
      'Market & Tool revenue breakdown across USD, EUR, GBP, CAD, and global jurisdictions.',
      'Core Web Vitals health monitor cross-referencing monetization changes with LCP, CLS, and INP metrics.',
      'Ad Inventory Manager with interactive preview, slot toggles, and Protected Zone verification inspector.',
    ],
    architectureNotes: 'Provides full operational visibility and instant control over global commercial operations.',
  },
];
